import React from "react"
import { pqIndexBandName } from "./constants"

// Semicircular needle gauge for the overall PQ Index score, ported to SVG from
// the v2 PDF report gauge (app/services/pdf_services/live_live/v2/components/gauge.rb).
// Palette, thresholds, band labels and the target caption are kept identical to
// the PDF so the dashboard and the downloaded report read the same.

// Arc band colours (PDF: palette.rb GAUGE_*)
const RED = "#DC3B23" // 0–50
const ORANGE = "#EBA23C" // 50–65
const YELLOW = "#F6D54C" // 65–85
const GREEN = "#00956B" // 85–100

const NEEDLE = "#3A3A3A"
const BRAND_PURPLE = "#2D2D7F" // the big score number
const LABEL_GREY = "#8A8A8A" // "/100", end labels, target caption

// Score → label colour and band name (PDF: gauge_score_color / GAUGE_BANDS).
const labelColour = score => {
  if (score >= 85) return GREEN
  if (score >= 65) return YELLOW
  if (score >= 50) return ORANGE
  return RED
}

// Geometry (proportional to the PDF's 78/59 outer/inner radii).
const CX = 110
const CY = 105
const OUTER_R = 88
const INNER_R = 68
const BAND_R = (OUTER_R + INNER_R) / 2 // stroked centre-line radius
const BAND_W = OUTER_R - INNER_R
const CAP_R = BAND_W / 2
const NEEDLE_LEN = OUTER_R * 0.9
const HUB_R = 6
const HALF_W = 4 // needle base half-width

// value 0 → 180° (left end), value 100 → 0° (right end)  (PDF: gauge_angle_rad)
const valueToDeg = value => 180 - (Math.max(0, Math.min(100, value)) / 100) * 180

const polar = (radius, deg) => {
  const rad = (Math.PI / 180) * deg
  return { x: CX + radius * Math.cos(rad), y: CY - radius * Math.sin(rad) }
}

// Stroked arc along the band centre-line, from one value to a higher value
// (left → over the top → right, i.e. clockwise in SVG's y-down space).
const bandPath = (from, to) => {
  const a = polar(BAND_R, valueToDeg(from))
  const b = polar(BAND_R, valueToDeg(to))
  return `M ${a.x} ${a.y} A ${BAND_R} ${BAND_R} 0 0 1 ${b.x} ${b.y}`
}

const BANDS = [
  { from: 0, to: 50, colour: RED },
  { from: 50, to: 65, colour: ORANGE },
  { from: 65, to: 85, colour: YELLOW },
  { from: 85, to: 100, colour: GREEN }
]

const TARGET_TEXT = "Target: 85 and above"

function PqIndexGauge({ score, targetText = TARGET_TEXT }) {
  if (score == null || Number.isNaN(Number(score))) return null

  const value = Math.round(Number(score))
  const deg = valueToDeg(value)
  const tip = polar(NEEDLE_LEN, deg)
  const rad = (Math.PI / 180) * deg
  const base1 = { x: CX + HALF_W * Math.cos(rad + Math.PI / 2), y: CY - HALF_W * Math.sin(rad + Math.PI / 2) }
  const base2 = { x: CX + HALF_W * Math.cos(rad - Math.PI / 2), y: CY - HALF_W * Math.sin(rad - Math.PI / 2) }
  const leftCap = polar(BAND_R, valueToDeg(0))
  const rightCap = polar(BAND_R, valueToDeg(100))

  return (
    <div
      className="pq-gauge"
      style={{ width: "100%", textAlign: "center" }}>
      <svg viewBox="0 0 220 135" width="100%" role="img" aria-label={`PQ Index ${value} out of 100, ${pqIndexBandName(value)}`}>
        {/* Coloured arc bands */}
        {BANDS.map(band => (
          <path key={band.colour} d={bandPath(band.from, band.to)} stroke={band.colour} strokeWidth={BAND_W} fill="none" />
        ))}
        {/* Rounded end caps (PDF: gauge_arc_caps) */}
        <circle cx={leftCap.x} cy={leftCap.y} r={CAP_R} fill={RED} />
        <circle cx={rightCap.x} cy={rightCap.y} r={CAP_R} fill={GREEN} />

        {/* Needle + hub */}
        <polygon points={`${tip.x},${tip.y} ${base1.x},${base1.y} ${base2.x},${base2.y}`} fill={NEEDLE} />
        <circle cx={CX} cy={CY} r={HUB_R} fill={NEEDLE} />

        {/* End labels */}
        <text x={CX - OUTER_R} y={CY + 20} textAnchor="middle" fontSize="11" fill={LABEL_GREY}>0</text>
        <text x={CX + OUTER_R} y={CY + 20} textAnchor="middle" fontSize="11" fill={LABEL_GREY}>100</text>
      </svg>

      <div
        className="pq-gauge-label text-uppercase"
        style={{
 color: labelColour(value), fontWeight: 700, letterSpacing: "1px", marginTop: "-8px"
}}>
        {pqIndexBandName(value)}
      </div>
      <div className="pq-gauge-score" style={{ marginTop: "4px" }}>
        <span style={{
 color: BRAND_PURPLE, fontSize: "2.75rem", fontWeight: 700, lineHeight: 1
}}>
          {value}
        </span>
        <span style={{ color: LABEL_GREY, fontSize: "1.1rem", fontWeight: 700 }}>/100</span>
      </div>
      <div className="pq-gauge-target" style={{ color: LABEL_GREY, fontSize: "0.8rem", marginTop: "4px" }}>
        {targetText}
      </div>
    </div>
  )
}

export default PqIndexGauge
